import { Vector2, Vector2Tuple, Vector3 } from 'three';

import { getOrientedBox2Corners } from '@spazious/dollhouse-engine';

import { FEnsembleBase } from '../fensembles/index.js';
import {
  Schema,
  SchemaFieldBoolean,
  SchemaFieldEnum,
  SchemaFieldNumber,
  SchemaFieldTypesMapping,
  SchemaValues,
} from '../schema/index.js';
import { FInstanceEnsembleNode, FInstanceNode } from '../types/index.js';
import { truncateDecimals } from '../utils/mathHelpers.js';

import {
  FArrangementBase,
  FArrangementBaseSchema,
  FArrangementBaseSchemaFields,
} from './FArrangementBase.js';

const TAU = Math.PI * 2;
const vec2 = new Vector2();
const vec3 = new Vector3();

export interface FArrangementGridSchemaFields extends FArrangementBaseSchemaFields {
  fill: SchemaFieldBoolean;
  direction: SchemaFieldEnum<string>;
  elements: SchemaFieldNumber;
  elementRotation: SchemaFieldNumber;
  columns: SchemaFieldNumber;
  columnsDistance: SchemaFieldNumber;
  rows: SchemaFieldNumber;
  rowsDistance: SchemaFieldNumber;
  displacement: SchemaFieldNumber;
  aisles: SchemaFieldNumber;
  aislesWidth: SchemaFieldNumber;
  aislesDirection: SchemaFieldEnum<string>;
  crisscross: SchemaFieldBoolean;
}

export interface FArrangementGridSchema extends FArrangementBaseSchema {
  fields: FArrangementGridSchemaFields;
}

export interface FArrangementGridValues {
  elementSize: SchemaFieldTypesMapping['vec2'];
  maxElements: SchemaFieldTypesMapping['number'];
}

export type FArrangementGridAttributes = Partial<SchemaValues<FArrangementGridSchemaFields>> &
  FArrangementGridValues;

export abstract class FArrangementGrid<
  TSchema extends FArrangementGridSchema = FArrangementGridSchema,
  TValues extends Record<string, any> = {},
  TFEnsembleSchema extends Schema = Schema,
  TFEnsembleValues extends Record<string, any> = {}
> extends FArrangementBase<
  TSchema,
  TValues & FArrangementGridValues,
  TFEnsembleSchema,
  TFEnsembleValues
> {
  usesEnsembles: boolean = false;
  ensembles: FInstanceEnsembleNode[] = [];

  constructor(
    schema: TSchema,
    initialValues?: FArrangementGridAttributes & TValues & TFEnsembleValues,
    fcomposition?: FEnsembleBase<TFEnsembleSchema, TFEnsembleValues>
  ) {
    super(
      schema,
      {
        maxElements: Infinity,
        elementSize: [0, 0],
        ...(initialValues as any),
      },
      fcomposition
    );
    this.setValue('getMaxRowDistance', this.getMaxRowDistance);
    this.setValue('getMaxColumnDistance', this.getMaxColumnDistance);

    this.setValue('getMaxAislesCount', this.getMaxAislesCount);
    this.setValue('getMaxAisleWidth', this.getMaxAisleWidth);
  }

  /**
   * Calculate number of grid elements as per current value of
   * - number of aisles and aislesWidth
   * - column and row distances
   * - elementSize
   */
  getMaxGridElements = () => {
    const {
      size,
      columnsDistance,
      rowsDistance,
      aisles,
      aislesWidth,
      aislesDirection,
      crisscross,
      direction,
    } = this.getValues() as SchemaValues<FArrangementGridSchemaFields> & FArrangementGridValues;
    const [elementSizeX, elementSizeY] = this.getElementSize();
    const [sizeX, sizeY] = size;
    const iXGap = columnsDistance + elementSizeX;
    const iYGap = rowsDistance + elementSizeY;

    let columnAisles = aislesDirection === 'column' ? aisles : 0;
    let rowAisles = aislesDirection === 'row' ? aisles : 0;

    const groupSizeX = (sizeX - columnAisles * aislesWidth) / (columnAisles + 1);
    const columnsPerGroup =
      groupSizeX >= elementSizeX ? 1 + Math.floor((groupSizeX - elementSizeX) / iXGap) : 0;
    const columns = columnsPerGroup * (columnAisles + 1);

    const groupSizeY = (sizeY - rowAisles * aislesWidth) / (rowAisles + 1);
    const rowsPerGroup =
      groupSizeY >= elementSizeY ? 1 + Math.floor((groupSizeY - elementSizeY) / iYGap) : 0;
    const rows = rowsPerGroup * (rowAisles + 1);

    if (crisscross) {
      const dirRow = /^row/.test(direction);
      if (dirRow) {
        // alternate rows have one element less
        return rows * columns - Math.floor(rows / 2) * 1;
      } else {
        // alternate columns have one element less
        return rows * columns - Math.floor(columns / 2) * 1;
      }
    }

    return columns * rows;
  };

  /**
   * Calculate max row distance based on current aisles count and aisles width
   * such that at least two rows of furnitures are present in each group
   */
  getMaxRowDistance = () => {
    const { size, aislesWidth, aisles, aislesDirection } =
      this.getValues() as SchemaValues<FArrangementGridSchemaFields> & FArrangementGridValues;
    const [, elementSizeY] = this.getElementSize();
    const [, sizeY] = size;
    if (aisles === 0 || aislesDirection !== 'row') {
      return Math.max(0, sizeY - elementSizeY * 2);
    }

    const groupSizeY = (sizeY - aisles * aislesWidth) / (aisles + 1);

    return Math.max(0, groupSizeY - 2 * elementSizeY);
  };

  /**
   * Calculate max column distance based on current aisles count and aisles width
   * such that at least two columns of furnitures are present in each group
   */
  getMaxColumnDistance = () => {
    const { size, aislesWidth, aisles, aislesDirection } =
      this.getValues() as SchemaValues<FArrangementGridSchemaFields> & FArrangementGridValues;
    const [elementSizeX] = this.getElementSize();
    const [sizeX] = size;
    if (aisles === 0 || aislesDirection !== 'column') {
      return Math.max(0, sizeX - elementSizeX * 2);
    }

    const groupSizeX = (sizeX - aisles * aislesWidth) / (aisles + 1);

    return Math.max(0, groupSizeX - 2 * elementSizeX);
  };

  getMaxAislesCount = () => {
    const { size, aislesWidth, aislesDirection } =
      this.getValues() as SchemaValues<FArrangementGridSchemaFields> & FArrangementGridValues;
    const elementSize = this.getElementSize();
    const [sizeX, sizeY] = size;
    let maxAisles;
    if (aislesWidth === 0) {
      maxAisles = 0;
    } else {
      if (aislesDirection === 'column') {
        maxAisles = Math.floor((sizeX - elementSize[0]) / (aislesWidth + elementSize[0]));
      } else {
        maxAisles = Math.floor((sizeY - elementSize[1]) / (aislesWidth + elementSize[1]));
      }
    }
    return maxAisles;
  };

  getMaxAisleWidth = () => {
    const { size, aislesDirection, aisles, columnsDistance, rowsDistance } =
      this.getValues() as SchemaValues<FArrangementGridSchemaFields> & FArrangementGridValues;

    const elementSize = this.getElementSize();
    const [sizeX, sizeY] = size;
    let width;
    if (aisles === 0) {
      if (aislesDirection === 'column') {
        width = Math.max(0, sizeX - 2 * elementSize[0] - columnsDistance);
      } else {
        width = Math.max(0, sizeY - 2 * elementSize[1] - rowsDistance);
      }
    } else {
      if (aislesDirection === 'column') {
        width = Math.max(0, (sizeX - (aisles + 1) * elementSize[0]) / aisles);
      } else {
        width = Math.max(0, (sizeY - (aisles + 1) * elementSize[1]) / aisles);
      }
    }

    return truncateDecimals(width, 2);
  };

  updateObjectMeasurements(): void {
    const bbox = this.getInstanceBoundingBox();

    bbox.getSize(vec3);
    const elementSize = new Vector2(vec3.x, vec3.z);

    // TODO fix typings
    this.updateValues({
      elementSize: elementSize.toArray(),
    } as any);
  }

  getMinimumSize() {
    const bbox = this.getInstanceBoundingBox();
    bbox.getSize(vec3);
    const minimumSize = new Vector2(vec3.x, vec3.z);
    return minimumSize.toArray();
  }

  getMaxIterations(maxColumns: number, maxRows: number, ignoreMaxElements: boolean = false) {
    const { elements } = this.getValue('elements');
    return this.getSafeIterations(
      Math.min(ignoreMaxElements ? Infinity : elements, maxColumns * maxRows)
    );
  }

  getNodeAttrs(
    // eslint-disable-next-line unused-imports/no-unused-vars, no-unused-vars
    params: {
      position: Vector2Tuple;
      row: number;
      column: number;
    },
    // eslint-disable-next-line unused-imports/no-unused-vars, no-unused-vars
    idx: number
  ): SchemaValues<TFEnsembleSchema['fields'] & TFEnsembleValues> {
    return this.getValues();
  }

  protected getCompositionNodes(
    /* eslint-disable unused-imports/no-unused-vars, no-unused-vars */
    params: {
      position: Vector2Tuple;
      row: number;
      column: number;
    },
    idx: number
    /* eslint-enable unused-imports/no-unused-vars, no-unused-vars */
  ): FInstanceNode[] {
    throw new Error('FArrangementGrid.getCompositionNodes Method not implemented.');
  }

  getElementSize() {
    return this.getValue('elementSize');
  }

  computeNodes(ignoreMaxElements: boolean = false): FInstanceNode[] {
    if (this.getObjects().length === 0) {
      return [];
    }

    const {
      orientation,
      mask,
      size,
      columnsDistance,
      rowsDistance,
      direction,
      aisles,
      aislesWidth,
      aislesDirection,
      crisscross,
    } = this.getValues() as SchemaValues<FArrangementGridSchemaFields> & FArrangementGridValues;
    const [sizeX, sizeY] = size;

    const elementSize = this.getElementSize();

    const iXGap = columnsDistance + elementSize[0];
    const iYGap = rowsDistance + elementSize[1];

    let groupSizeX = sizeX;
    let groupSizeY = sizeY;
    let rowGroups = 1,
      columnGroups = 1;
    if (aisles > 0) {
      if (aislesDirection === 'column') {
        groupSizeX = (sizeX - aisles * aislesWidth) / (aisles + 1);
        columnGroups = aisles + 1;
      } else {
        groupSizeY = (sizeY - aisles * aislesWidth) / (aisles + 1);
        rowGroups = aisles + 1;
      }
    }
    const columnsPerGroup =
      groupSizeX >= elementSize[0] ? 1 + Math.floor((groupSizeX - elementSize[0]) / iXGap) : 0;

    const rowsPerGroup =
      groupSizeY >= elementSize[1] ? 1 + Math.floor((groupSizeY - elementSize[1]) / iYGap) : 0;
    let columns = columnsPerGroup * columnGroups;
    let rows = rowsPerGroup * rowGroups;
    const dirRow = /^row/.test(direction);

    const maxElements = this.getMaxIterations(columns, rows, ignoreMaxElements);

    const theta = orientation * TAU;

    const pivot = mask?.length ? this.getMaskPivot(mask) : new Vector2(0, 0);

    const dirReverse = /-reverse$/.test(direction);

    let xStart = -sizeX / 2;
    let yStart = -sizeY / 2;
    if (dirReverse) {
      if (dirRow) {
        yStart = sizeY / 2;
      } else {
        xStart = sizeX / 2;
      }
    }

    let column = 0;
    let row = 0;
    let x, y;
    let maxIterations = rows * columns;
    if (crisscross) {
      if (dirRow) {
        // alternate rows have one element less
        maxIterations = rows * columns - Math.floor(rows / 2) * 1;
      } else {
        // alternate columns have one element less
        maxIterations = rows * columns - Math.floor(columns / 2) * 1;
      }
    }
    const nodes: FInstanceNode[] = [];
    const ensembles: FInstanceEnsembleNode[] = [];
    let gridElements = 0;

    for (let i = 0; i < maxIterations && gridElements < maxElements; i++) {
      let columnGroupIndex = Math.floor(column / columnsPerGroup);
      let rowGroupIndex = Math.floor(row / rowsPerGroup);

      let prevGroupsSizeX =
        columnGroupIndex * (elementSize[0] + (columnsPerGroup - 1) * iXGap) + // size of furniture group
        columnGroupIndex * aislesWidth; // gaps from aisles

      let prevGroupsSizeY =
        rowGroupIndex * (elementSize[1] + (rowsPerGroup - 1) * iYGap) + // size of furniture group
        rowGroupIndex * aislesWidth; // gaps from aisles

      let xPos = prevGroupsSizeX + (column % columnsPerGroup) * iXGap + elementSize[0] * 0.5;
      let yPos = prevGroupsSizeY + (row % rowsPerGroup) * iYGap + elementSize[1] * 0.5;

      if (dirReverse) {
        if (dirRow) {
          yPos = -yPos;
        } else {
          xPos = -xPos;
        }
      }

      x = xStart + xPos;
      y = yStart + yPos;

      let maxColumns = columns;
      let maxRows = rows;
      if (crisscross) {
        if (dirRow) {
          if (row % 2 === 1) {
            x += iXGap * 0.5;
            maxColumns -= 1;
          }
        } else {
          if (column % 2 === 1) {
            y += iYGap * 0.5;
            maxRows -= 1;
          }
        }
      }

      const position = vec2.set(x, y).rotateAround(new Vector2(0, 0), theta).add(pivot).toArray();

      let isWithinMask = true;
      if (mask?.length) {
        const boxCorners = getOrientedBox2Corners(position, elementSize, theta);

        isWithinMask = this.polygonContains(mask, [boxCorners]);
      }

      if (isWithinMask) {
        nodes.push(...this.getCompositionNodes({ position, row, column }, gridElements));
        ensembles.push(...this.getEnsembles({ position, row, column }, gridElements));
        gridElements++;
      }

      if (dirRow) {
        if (column === maxColumns - 1) {
          row++;
          column = 0;
        } else {
          column++;
        }
      } else {
        if (row === maxRows - 1) {
          column++;
          row = 0;
        } else {
          row++;
        }
      }
    }
    this.ensembles = ensembles;

    return nodes as [FInstanceNode];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getEnsembles(
    // eslint-disable-next-line no-unused-vars
    _params: {
      position: Vector2Tuple;
      row: number;
      column: number;
    },
    // eslint-disable-next-line no-unused-vars
    _idx: number
  ): FInstanceEnsembleNode[] {
    return [];
  }
}
